import dayjs from 'dayjs';
import type { LowPrices } from '@aviata/platform-client';
import type { Dict } from '@aviata/platform-types';

function createIntersectedAirlines(
  departureAirlinesFilter: LowPrices.V2.Filter,
  arrivalAirlinesFilter: LowPrices.V2.Filter,
) {
  const result: LowPrices.V2.Filter = {};

  for (const key in departureAirlinesFilter) {
    if (arrivalAirlinesFilter[key]) {
      result[key] = departureAirlinesFilter[key].filter(airline =>
        arrivalAirlinesFilter[key].includes(airline),
      );
    }
  }
  return result;
}

function getUnionDictAirlines(
  departureDictAirlines: Dict.Airlines.Airline[],
  arrivalDictAirlines: Dict.Airlines.Airline[],
) {
  const mergedArray = [...departureDictAirlines, ...arrivalDictAirlines];

  const uniqueAirlines = new Map(
    mergedArray.map(airline => [airline.iata, airline]),
  );

  return Array.from(uniqueAirlines.values());
}

/**
 * Fills the array of intersected airlines.
 * @param airlines - An array of intersected airlines.
 * @param dict - An array of dictionary airlines.
 * @returns An array of intersected airlines.
 */
function fillIntersectedAirlines(
  airlines: LowPrices.V2.Filter,
  dict: Dict.Airlines.Airline[],
) {
  const result: Record<string, Dict.Airlines.Airline[]> = {};

  for (const key in airlines) {
    result[key] = dict.filter(airline => airlines[key].includes(airline.iata));
  }

  return result;
}

const moveAirlineToTopMutation = (
  airlines: Dict.Airlines.Airline[],
  index: number,
) => {
  if (index < 0 || index >= airlines.length) return airlines;
  const [item] = airlines.splice(index, 1);
  airlines.unshift(item);
};

const getNewPricesArr = (
  searchMap: LowPrices.V2.SearchCalendarResponse,
  selectedAirline: Dict.Airlines.Airline | null,
) => {
  return searchMap.dates.reduce((acc, val) => {
    if (selectedAirline) {
      const filteredAirlinePrices = val.transfers?.[0]?.airlines?.find(
        airline => airline.iata === selectedAirline?.iata,
      );
      if (!filteredAirlinePrices) {
        return { ...acc };
      }
      return {
        ...acc,
        [val.date]: {
          price: val.offers[filteredAirlinePrices.cheap]?.price,
        },
      };
    }
    return { ...acc, [val.date]: { price: val.offers[val.cheap]?.price } };
  }, {});
};

const formatSelectedDates = (dates: string[], isDatePricesExist: boolean) => {
  if (!dates.length) {
    return null;
  }
  const [first, second] = dates;
  const dayjsFirst = dayjs(first);
  const dayjsSecond = dayjs(second);
  const formattedFirst = dayjsFirst.format('D MMMM');

  if (!second) {
    return formattedFirst;
  }

  const isSameMonth = dayjsFirst.isSame(dayjsSecond, 'month');
  const formattedSecond = dayjsSecond.format('D MMMM');

  if (isDatePricesExist) {
    return `${formattedFirst} – ${formattedSecond}`;
  }

  const formattedSecondDate = dayjsSecond.format('D MMM').replace('.', '');

  return isSameMonth
    ? `${dayjsFirst.format('D')} – ${formattedSecondDate}`
    : `${dayjsFirst.format('D MMM').replace('.', '')} – ${formattedSecondDate}`;
};

export const calendarLib = {
  createIntersectedAirlines,
  getUnionDictAirlines,
  fillIntersectedAirlines,
  moveAirlineToTopMutation,
  getNewPricesArr,
  formatSelectedDates,
};
